/**
 * Patient media, processed in the browser before upload (keyon has no image tooling).
 *
 * Images: the capture time is read from EXIF first, then the picture is re-encoded once to WebP,
 * scaled so its long edge is at most MAX_EDGE (HEIC included, through imageConversion.js). The
 * canvas re-encode drops every EXIF field, GPS location included: a phone photo taken in the
 * patient's home carries the home's coordinates, and nothing should store them. A 320 px
 * thumbnail is drawn from the converted image.
 *
 * Videos are uploaded as picked (the server judges the bytes). A frame near 1 s becomes the
 * thumbnail, and the duration is read from the same <video> element.
 *
 * processMediaFile(file, captureSource) resolves to what commitMediaBatch takes for one file.
 */
import { convertUploadFile, isHeic, isWithinInputLimit, REPICK_MESSAGE, WEBP_QUALITY, fitWithin } from '@/utils/imageConversion';
import { validateVideoFile } from '@/utils/videoUploadPlan';

export const MAX_EDGE = 2560;
export const THUMB_EDGE = 320;
// Prototype stand-in for the server's video policy (video_upload.php?action=config).
export const VIDEO_POLICY = { max_bytes: 500 * 1024 * 1024, videos_per_message: 99 };

const IMAGE_TYPES = ['image/jpeg', 'image/png'];

// Browsers report HEIC as "" or application/octet-stream on some platforms; trust the extension
// then, so the check and the HEIC conversion both see an image/heic file.
export function normalizePick(file) {
  const type = String((file && file.type) || '').toLowerCase();
  if (!file || !/\.(heic|heif)$/i.test(file.name || '') || (type && type !== 'application/octet-stream')) return file;
  if (typeof File !== 'function' || !(file instanceof Blob)) return file;
  return new File([file], file.name, { type: 'image/heic', lastModified: file.lastModified });
}

export function isImageFile(file) {
  return !!file && (IMAGE_TYPES.includes(String(file.type || '').toLowerCase()) || isHeic(file));
}

export function isVideoPick(file) {
  return !!file && (String(file.type || '').toLowerCase().startsWith('video/') || /\.(mp4|m4v|mov)$/i.test(file.name || ''));
}

/** What to do with a pick before any work: ok, or a message the person can act on. */
export function checkPick(picked) {
  const file = normalizePick(picked);
  if (isVideoPick(file)) return validateVideoFile(file, VIDEO_POLICY);
  if (isImageFile(file)) return isWithinInputLimit(file) ? { ok: true } : { ok: false, code: 'too_large', message: REPICK_MESSAGE };
  return { ok: false, code: 'unsupported', message: `"${(file && file.name) || 'file'}" is not a photo or video. Choose a JPG, PNG, HEIC, MP4 or MOV.` };
}

export async function processMediaFile(picked, captureSource = 'gallery') {
  const file = normalizePick(picked);
  const check = checkPick(file);
  if (!check.ok) throw Object.assign(new Error(check.message), { code: check.code });
  return isVideoPick(file) ? processVideo(file, captureSource) : processImage(file, captureSource);
}

export async function processImage(file, captureSource = 'gallery') {
  const exifDate = await readExifDateTimeOriginal(file).catch(() => null);
  const full = await convertUploadFile(file, { maxEdge: MAX_EDGE });
  const { width, height, thumb } = await thumbnailFromImage(full);
  return {
    kind: 'image',
    full,
    thumb,
    content_type: full.type,
    byte_size: full.size,
    width,
    height,
    duration_sec: null,
    captured_at: captureTime(exifDate, file),
    capture_source: captureSource,
    original_filename: file.name || null,
  };
}

export async function processVideo(file, captureSource = 'gallery') {
  const { thumb, width, height, duration } = await videoFrame(file);
  return {
    kind: 'video',
    full: file,
    thumb,
    content_type: file.type || 'video/mp4',
    byte_size: file.size,
    width,
    height,
    duration_sec: duration ? Math.round(duration * 100) / 100 : null,
    captured_at: captureTime(null, file),
    capture_source: captureSource,
    original_filename: file.name || null,
  };
}

/** EXIF DateTimeOriginal, else the file's own time, else now. ISO, UTC. */
export function captureTime(exifDate, file) {
  if (exifDate) return exifDate.toISOString();
  if (file && file.lastModified) return new Date(file.lastModified).toISOString();
  return new Date().toISOString();
}

async function thumbnailFromImage(fileOrBlob) {
  const bmp = await decode(fileOrBlob);
  try {
    const w = bmp.naturalWidth || bmp.width;
    const h = bmp.naturalHeight || bmp.height;
    const t = fitWithin(w, h, THUMB_EDGE);
    const thumb = await drawToWebp(bmp, t.width, t.height);
    return { width: w, height: h, thumb };
  } finally {
    if (typeof bmp.close === 'function') bmp.close();
  }
}

function decode(blob) {
  if (typeof createImageBitmap === 'function') return createImageBitmap(blob);
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image could not be decoded')); };
    img.src = url;
  });
}

export function drawToWebp(source, width, height) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return Promise.reject(new Error('Canvas 2D context unavailable'));
  ctx.drawImage(source, 0, 0, width, height);
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('WebP encoding failed'))), 'image/webp', WEBP_QUALITY);
  });
}

function videoFrame(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement('video');
    v.muted = true;
    v.playsInline = true;
    v.preload = 'metadata';
    let done = false;
    const finish = (fn) => (arg) => { if (done) return; done = true; URL.revokeObjectURL(url); fn(arg); };
    const ok = finish(resolve);
    const fail = finish(reject);
    v.onloadedmetadata = () => {
      // Near 1 s shows something other than a black first frame; short clips use their middle.
      const d = Number.isFinite(v.duration) ? v.duration : 0;
      v.currentTime = d > 2 ? 1 : d / 2;
    };
    v.onseeked = async () => {
      try {
        const t = fitWithin(v.videoWidth, v.videoHeight, THUMB_EDGE);
        const thumb = await drawToWebp(v, t.width || THUMB_EDGE, t.height || 180);
        ok({ thumb, width: v.videoWidth || null, height: v.videoHeight || null, duration: Number.isFinite(v.duration) ? v.duration : null });
      } catch (e) { fail(e); }
    };
    v.onerror = () => fail(new Error(`"${file.name || 'video'}" could not be read. Choose an MP4 or MOV video.`));
    v.src = url;
  });
}

/* ---------------------------------------------------------------- EXIF */

const EXIF_TAG_POINTER = 0x8769;
const TAG_DATETIME_ORIGINAL = 0x9003;
const TAG_DATETIME = 0x0132;

/**
 * Reads EXIF DateTimeOriginal (or DateTime) from a JPEG or HEIC. Both carry a TIFF block after an
 * "Exif\0\0" marker, so the first one found in the head of the file is parsed. Returns a Date in
 * the device's local time, or null. Reading only; nothing here writes EXIF anywhere.
 */
export async function readExifDateTimeOriginal(file) {
  const head = new Uint8Array(await readHead(file, 512 * 1024));
  const at = findExifMarker(head);
  if (at < 0) return null;
  return parseTiffDate(new DataView(head.buffer, head.byteOffset + at + 6, head.byteLength - at - 6));
}

function readHead(file, n) {
  const part = file.slice(0, n);
  if (typeof part.arrayBuffer === 'function') return part.arrayBuffer();
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(r.error);
    r.readAsArrayBuffer(part);
  });
}

function findExifMarker(b) {
  for (let i = 0; i + 6 < b.length; i += 1) {
    if (b[i] === 0x45 && b[i + 1] === 0x78 && b[i + 2] === 0x69 && b[i + 3] === 0x66 && b[i + 4] === 0 && b[i + 5] === 0) return i;
  }
  return -1;
}

export function parseTiffDate(view) {
  if (view.byteLength < 8) return null;
  const order = view.getUint16(0);
  const le = order === 0x4949;
  if (!le && order !== 0x4d4d) return null;
  const u16 = (o) => view.getUint16(o, le);
  const u32 = (o) => view.getUint32(o, le);
  const readIfd = (off) => {
    if (off + 2 > view.byteLength) return [];
    const n = u16(off);
    const out = [];
    for (let i = 0; i < n; i += 1) {
      const e = off + 2 + i * 12;
      if (e + 12 > view.byteLength) break;
      out.push({ tag: u16(e), type: u16(e + 2), count: u32(e + 4), value: u32(e + 8) });
    }
    return out;
  };
  const ascii = (entry) => {
    if (entry.type !== 2 || entry.count < 19 || entry.value + 19 > view.byteLength) return null;
    let s = '';
    for (let i = 0; i < 19; i += 1) s += String.fromCharCode(view.getUint8(entry.value + i));
    return s;
  };
  const ifd0 = readIfd(u32(4));
  const ptr = ifd0.find((e) => e.tag === EXIF_TAG_POINTER);
  const exif = ptr ? readIfd(ptr.value) : [];
  const entry = exif.find((e) => e.tag === TAG_DATETIME_ORIGINAL) || ifd0.find((e) => e.tag === TAG_DATETIME);
  const s = entry && ascii(entry);
  const m = s && /^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})$/.exec(s);
  if (!m) return null;
  const d = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]);
  return Number.isNaN(d.getTime()) || +m[1] < 1990 ? null : d;
}
